import { CheckCircle, Sparkle, Wrench } from "@phosphor-icons/react";
import { useMutation, useQuery } from "convex/react";
import { useState } from "react";

import { api } from "../../convex/_generated/api";
import type { Id } from "../../convex/_generated/dataModel";
import type { BusinessMembership, ConfigurationSuggestion } from "./types";
import { WorkspaceSetupDetail } from "../workspace/WorkspaceSetupDetail";

export function ConfigurationAssistantPage({
  business,
  embedded = false,
  onSignOut,
}: {
  business: BusinessMembership;
  embedded?: boolean;
  onSignOut: () => void;
}) {
  const businessId = business.businessId as Id<"businesses">;
  const latest = useQuery(
    api.businessConfigurationAssistant.getLatestRecommendation,
    { businessId },
  ) as ConfigurationSuggestion | null | undefined;

  const panel = (
    <AssistantPanel
      businessId={businessId}
      latest={latest}
      owner={business.role === "OWNER"}
    />
  );

  if (embedded) {
    return (
      <WorkspaceSetupDetail
        description="Draft a recommendation for owner review without changing canonical business state automatically."
        eyebrow="Configuration assistant"
        title="Review a configuration recommendation"
      >
        {panel}
      </WorkspaceSetupDetail>
    );
  }

  return (
    <main className="foundation-shell business-setup-shell">
      <section aria-live="polite" className="status-panel">
        <span className="status-label">Configuration assistant</span>
        <strong>{business.name}</strong>
        <span>{business.role} workspace</span>
        <button onClick={onSignOut} type="button">
          Sign out
        </button>
      </section>
      {panel}
    </main>
  );
}

function AssistantPanel({
  businessId,
  latest,
  owner,
}: {
  businessId: Id<"businesses">;
  latest: ConfigurationSuggestion | null | undefined;
  owner: boolean;
}) {
  const draftRecommendation = useMutation(
    api.businessConfigurationAssistant.draftRecommendation,
  );
  const applyRecommendation = useMutation(
    api.businessConfigurationAssistant.applyRecommendation,
  );
  const [description, setDescription] = useState("I run a plumbing company.");
  const [suggestion, setSuggestion] = useState<
    ConfigurationSuggestion | null | undefined
  >(latest);
  const [feedback, setFeedback] = useState("Ready");
  const visibleSuggestion = suggestion ?? latest;

  async function draft() {
    if (!owner) return;
    setFeedback("Drafting");
    try {
      const result = (await draftRecommendation({
        businessId,
        clientRequestId: `nk42-ui-${Date.now()}`,
        businessDescription: description,
      })) as ConfigurationSuggestion;
      setSuggestion(result);
      setFeedback("Review required");
    } catch {
      setFeedback("Draft denied");
    }
  }

  async function apply() {
    if (
      !owner ||
      !visibleSuggestion ||
      visibleSuggestion.status !== "DRAFTED"
    ) {
      return;
    }
    setFeedback("Applying");
    try {
      const result = (await applyRecommendation({
        businessId,
        suggestionId: visibleSuggestion.suggestionId,
      })) as ConfigurationSuggestion;
      setSuggestion(result);
      setFeedback("Applied");
    } catch {
      setFeedback("Apply denied");
    }
  }

  return (
    <section className="business-setup-panel configuration-assistant-panel">
      <div className="business-setup-toolbar">
        <label>
          <span className="status-label">Business description</span>
          <textarea
            disabled={!owner}
            onChange={(event) => setDescription(event.target.value)}
            rows={4}
            value={description}
          />
        </label>
        <button disabled={!owner} onClick={() => void draft()} type="button">
          <Sparkle aria-hidden weight="bold" />
          Draft recommendation
        </button>
      </div>
      <SuggestionPanel
        feedback={feedback}
        onApply={apply}
        owner={owner}
        suggestion={visibleSuggestion}
      />
    </section>
  );
}

function SuggestionPanel({
  feedback,
  onApply,
  owner,
  suggestion,
}: {
  feedback: string;
  onApply: () => Promise<void>;
  owner: boolean;
  suggestion: ConfigurationSuggestion | null | undefined;
}) {
  if (!suggestion) {
    return (
      <article className="capability-card">
        <header>
          <Wrench aria-hidden />
          <div>
            <strong>{feedback}</strong>
            <span>No draft yet</span>
          </div>
        </header>
      </article>
    );
  }

  return (
    <article className="capability-card configuration-assistant-card">
      <header>
        <CheckCircle aria-hidden weight="fill" />
        <div>
          <strong>{suggestion.recommendedProfile}</strong>
          <span>{suggestion.status}</span>
        </div>
      </header>
      <p>{suggestion.rationale}</p>
      <CapabilityList
        label="Recommended"
        values={suggestion.recommendedCapabilities}
      />
      <CapabilityList
        label="Future only"
        values={suggestion.futureCapabilities}
      />
      <div className="capability-actions">
        <button
          disabled={!owner || suggestion.status !== "DRAFTED"}
          onClick={() => void onApply()}
          type="button"
        >
          <CheckCircle aria-hidden weight="bold" />
          Apply reviewed draft
        </button>
      </div>
    </article>
  );
}

function CapabilityList({
  label,
  values,
}: {
  label: string;
  values: string[];
}) {
  return (
    <div>
      <span className="status-label">{label}</span>
      <p>{values.length > 0 ? values.join(", ") : "None"}</p>
    </div>
  );
}
